// Generic kebab-menu (⋮ trigger + popover with one row per action).
// Used in the detail-panel headers for Archive / Mark complete and
// in the team roster for Set lead / Remove. Closes on outside click
// or Esc.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { MoreVertical } from "lucide-react";
import { DC } from "../sheets/createSlideOver/primitives";

export interface KebabAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void | Promise<void>;
  /** Visually mark the row as destructive (red on hover). */
  danger?: boolean;
  /** Render as a divider above this row instead of a button. */
  divider?: boolean;
  disabled?: boolean;
}

interface KebabMenuProps {
  actions: KebabAction[];
  title?: string;
  /** Anchor side. `right` aligns the menu's right edge with the
   *  trigger (default — matches the detail-panel layout where the
   *  trigger sits at the top-right). */
  align?: "left" | "right";
}

export function KebabMenu({ actions, title = "More actions", align = "right" }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        className="tp-icon-action"
        aria-label={title}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <MoreVertical size={14} />
      </button>
      {open ? (
        <div
          role="menu"
          className="tp-kebab-menu"
          style={{
            top: "calc(100% + 4px)",
            [align === "right" ? "right" : "left"]: 0,
          }}
        >
          {actions.map((a, idx) =>
            a.divider ? (
              <div
                key={`div-${idx}`}
                role="separator"
                style={{ height: 1, background: DC.border, margin: "4px 0" }}
              />
            ) : (
              <button
                key={a.label}
                type="button"
                role="menuitem"
                disabled={a.disabled}
                className={a.danger ? "tp-kebab-item danger" : "tp-kebab-item"}
                onClick={async () => {
                  setOpen(false);
                  await a.onClick();
                }}
              >
                {a.icon ? <span style={{ display: "inline-flex", color: a.danger ? "inherit" : DC.grey }}>{a.icon}</span> : null}
                <span>{a.label}</span>
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}
